import React, { useEffect, useRef } from "react";
import {
  GetIntellipayWidget,
  PostIntellipayStoreSuccess,
} from "../services/PaymentService";
import { useAxiosInstance } from "../services/Interceptor";
import { toast } from "react-toastify";

// Fallback poll for widget readiness if the <script> load event is missed.
const WIDGET_READY_POLL_INTERVAL_MS = 500;
const WIDGET_READY_MAX_POLL_ATTEMPTS = 20; // 10-second ceiling

const IntellipayStoreOnlyMode = (props) => {
  const apiInstance = useAxiosInstance();
  const isMountedRef = useRef(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    loadIntelliPayScript();

    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetIntellipayWidget = () => {
    window.__intellipayWidgetLoaded = false;
  };

  const attachWidgetCallbacks = () => {
    window.intellipay.initialize();
    window.intellipay.setStoreOnly(true);
    window.intellipay.runOnApproval(async (response) => {
      const storeResponse = await PostIntellipayStoreSuccess(
        apiInstance,
        response.custid,
      );

      if (storeResponse.success) {
        toast.success("Payment details updated successfully!", {
          position: "top-right",
        });
        // NOTE: also fires on the non-approval path below, which looks
        // wrong regardless of timing — out of scope here, flagging for
        // a separate ticket.
        props.onRegistrationSuccess();
      } else {
        toast.error("Failed to update payment details", {
          position: "top-right",
        });
      }
    });
    window.intellipay.runOnNonApproval((res) => {
      resetIntellipayWidget();
      props.onRegistrationSuccess();
    });
    window.__intellipayWidgetLoaded = true;
    props.onWidgetReady?.(true);
  };

  // `scriptEl` may be null if the script was already in the DOM — the poll
  // below is the only path in that case.
  const waitForWidgetReady = (scriptEl) => {
    let settled = false;

    const stopWaiting = () => {
      settled = true;
      if (scriptEl) scriptEl.removeEventListener("load", handleScriptLoad);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const finish = () => {
      if (settled || !isMountedRef.current) {
        stopWaiting();
        return;
      }
      stopWaiting();
      attachWidgetCallbacks();
    };

    const handleScriptLoad = () => {
      if (window.intellipay) finish();
    };

    if (scriptEl) {
      scriptEl.addEventListener("load", handleScriptLoad);
    }

    let attempts = 0;
    intervalRef.current = setInterval(() => {
      if (!isMountedRef.current) {
        stopWaiting();
        return;
      }
      attempts++;
      if (window.intellipay) {
        finish();
      } else if (attempts >= WIDGET_READY_MAX_POLL_ATTEMPTS) {
        stopWaiting();
        console.error(
          "Intellipay widget failed to load after maximum attempts",
        );
        // Tell the caller it failed too, or it'll spin forever waiting.
        props.onWidgetReady?.(false);
      }
    }, WIDGET_READY_POLL_INTERVAL_MS);
  };

  const loadIntelliPayScript = async () => {
    try {
      if (window.intellipay) {
        attachWidgetCallbacks();
        return;
      }

      const response = await GetIntellipayWidget(apiInstance);
      if (!isMountedRef.current) return;

      if (response.success && response.data) {
        const range = document.createRange();
        const fragment = range.createContextualFragment(response.data);

        let scriptEl = null;
        if (!document.querySelector("script[src*='intellipay']")) {
          scriptEl = fragment.querySelector("script[src*='intellipay']");
          document.documentElement.appendChild(fragment);
        }

        waitForWidgetReady(scriptEl);
      } else {
        console.error("Failed to retrieve IntelliPay script from backend");
        props.onWidgetReady?.(false);
      }
    } catch (error) {
      console.error("Error loading IntelliPay script:", error);
      props.onWidgetReady?.(false);
    }
  };

  return (
    <div>
      <input
        className="ipayfield"
        data-ipayname="account"
        type="hidden"
        value={0}
      />
      <input
        className="ipayfield"
        data-ipayname="email"
        type="hidden"
        value={props.userEmail}
      />
    </div>
  );
};

export default IntellipayStoreOnlyMode;
